import type { Direction, DungeonRoom, Vector2D } from '../../game/types';
import { shuffle } from './random';

export const CARDINAL_DIRECTIONS: {
  direction: Direction;
  dx: number;
  dy: number;
}[] = [
  { direction: 'NORTH', dx: 0, dy: -1 },
  { direction: 'SOUTH', dx: 0, dy: 1 },
  { direction: 'EAST', dx: 1, dy: 0 },
  { direction: 'WEST', dx: -1, dy: 0 },
];

export function gridKey(pos: Vector2D): string {
  return `${pos.x},${pos.y}`;
}

export function oppositeDirection(direction: Direction): Direction {
  switch (direction) {
    case 'NORTH':
      return 'SOUTH';
    case 'SOUTH':
      return 'NORTH';
    case 'EAST':
      return 'WEST';
    case 'WEST':
      return 'EAST';
  }
}

/** Connected cell set grown from center using randomized DFS frontier. */
export function generateConnectedCells(
  width: number,
  height: number,
  roomCount: number,
  rng: () => number,
): { start: Vector2D; cells: Vector2D[] } {
  const start = {
    x: Math.floor(width / 2),
    y: Math.floor(height / 2),
  };
  const cells = new Map<string, Vector2D>([[gridKey(start), start]]);
  const frontier: Vector2D[] = [start];

  while (cells.size < roomCount && frontier.length > 0) {
    const index = Math.floor(rng() * frontier.length);
    const current = frontier[index] as Vector2D;
    let expanded = false;

    for (const { dx, dy } of shuffle(rng, CARDINAL_DIRECTIONS)) {
      const next = { x: current.x + dx, y: current.y + dy };
      const inside = next.x >= 0 && next.y >= 0 && next.x < width && next.y < height;
      if (!inside || cells.has(gridKey(next))) {
        continue;
      }
      cells.set(gridKey(next), next);
      frontier.push(next);
      expanded = true;
      break;
    }

    if (!expanded) {
      frontier.splice(index, 1);
    }
  }

  return { start, cells: [...cells.values()] };
}

/** Add symmetric 4-direction links for adjacent generated rooms. */
export function connectAdjacentRooms(rooms: Record<string, DungeonRoom>): void {
  for (const room of Object.values(rooms)) {
    const { x, y } = room.gridPos;
    for (const { direction, dx, dy } of CARDINAL_DIRECTIONS) {
      const neighborId = gridKey({ x: x + dx, y: y + dy });
      const neighbor = rooms[neighborId];
      if (!neighbor) continue;
      if (!room.connections.some((link) => link.direction === direction)) {
        room.connections.push({ direction, targetRoomId: neighborId });
      }
      const reverse = oppositeDirection(direction);
      if (!neighbor.connections.some((link) => link.direction === reverse)) {
        neighbor.connections.push({ direction: reverse, targetRoomId: room.id });
      }
    }
  }
}

export function cellDistances(
  cells: readonly Vector2D[],
  start: Vector2D,
): Map<string, number> {
  const active = new Set(cells.map(gridKey));
  const distances = new Map<string, number>([[gridKey(start), 0]]);
  const queue: Vector2D[] = [start];
  while (queue.length > 0) {
    const current = queue.shift() as Vector2D;
    const distance = distances.get(gridKey(current)) ?? 0;
    for (const { dx, dy } of CARDINAL_DIRECTIONS) {
      const next = { x: current.x + dx, y: current.y + dy };
      const key = gridKey(next);
      if (!active.has(key) || distances.has(key)) continue;
      distances.set(key, distance + 1);
      queue.push(next);
    }
  }
  return distances;
}

/** Choose boss/teleporter randomly from the distant half of the graph. */
export function selectSpecialCells(
  cells: readonly Vector2D[],
  start: Vector2D,
  rng: () => number,
): { boss: Vector2D; teleporter: Vector2D } {
  const distances = cellDistances(cells, start);
  const candidates = cells.filter((cell) => gridKey(cell) !== gridKey(start));
  const maxDistance = Math.max(...candidates.map((cell) => distances.get(gridKey(cell)) ?? 0));
  const threshold = Math.max(2, Math.ceil(maxDistance * 0.5));
  const distant = candidates.filter((cell) => (distances.get(gridKey(cell)) ?? 0) >= threshold);
  const ordered =
    distant.length >= 2
      ? shuffle(rng, distant)
      : shuffle(rng, candidates).sort(
          (a, b) =>
            (distances.get(gridKey(b)) ?? 0) - (distances.get(gridKey(a)) ?? 0),
        );
  const teleporter = (ordered[0] ?? candidates[0]) as Vector2D;
  const boss = (
    ordered.find((cell) => gridKey(cell) !== gridKey(teleporter)) ??
    candidates.find((cell) => gridKey(cell) !== gridKey(teleporter))
  ) as Vector2D;
  return { boss, teleporter };
}
